import { NewsletterVariant, notEmpty, theme } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";

import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { ClientSideSuspense } from "scmp-app/components/common/client-side-suspense";
import { section as sectionData } from "scmp-app/data";
import { useIntersection } from "scmp-app/lib/hooks";
import type { seriesArticleArticle$key } from "scmp-app/queries/__generated__/seriesArticleArticle.graphql";
import type { seriesArticleQuery } from "scmp-app/queries/__generated__/seriesArticleQuery.graphql";

import { SeriesArticleContextProvider } from "./contexts";
import {
  BannerAdSlot,
  Container,
  MobileAdSlotOne,
  MobileAdSlotThree,
  MobileAdSlotTwo,
  NativeAdSlot,
  NewsletterWidgetContainer,
  NewsletterWidgetWrapper,
  SecondBannerAdSlot,
  ShareBar,
  ShareDivider,
  StyledContentNewsletterWidget,
  StyledContentShareWidget,
  StyledSeriesArticleBanner,
  StyledSeriesArticleItem,
  StyledSeriesArticleOtherScmpSeries,
  StyledSeriesArticlePager,
} from "./styles";

export type Props = {
  reference: seriesArticleArticle$key;
} & ArticleRenderChildProps;

export const query = graphql`
  query seriesArticleQuery($queueName: String!, $excludeEntityIds: [String]) {
    queue(filter: { name: $queueName }) {
      ...otherScmpSeriesSeriesArticleQueue @arguments(excludeEntityIds: $excludeEntityIds)
    }
  }
`;

export const SeriesArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<seriesArticleQuery>(query);
  const [isPagerSticky, setIsPagerSticky] = useState(false);

  const { captureIntersectionElement, intersection } = useIntersection({
    rootMargin: "0px 0px -15%",
    threshold: [0],
  });

  useEffect(() => {
    setIsPagerSticky(!intersection?.isIntersecting);
  }, [intersection]);

  const article = useFragment(
    graphql`
      fragment seriesArticleArticle on Article {
        entityId
        sections {
          value {
            entityId
          }
        }
        series {
          ...itemSeriesArticleSeriesItemResult
        }
        ...bannerSeriesArticleArticle
        ...contentNewsletterWidgetContent
        ...contentShareWidgetContent
        ...pagerSeriesArticleArticle
      }
    `,
    reference,
  );

  useEffect(() => {
    const articleSectionIds =
      article?.sections?.flatMap(
        sections => sections?.value?.map(value => value?.entityId ?? "") ?? [],
      ) ?? [];

    // Remove the news, comment, and magazine sections from the array
    const queueId = first(
      articleSectionIds.filter(
        sectionId =>
          !(
            [
              sectionData.news.entityId,
              sectionData.comment.entityId,
              sectionData.magazine.entityId,
            ] as string[]
          ).includes(sectionId),
      ),
    );

    loadQuery({
      excludeEntityIds: [article.entityId],
      queueName: `section_top_series_${queueId}`,
    });

    return disposeQuery;
  }, [article, loadQuery, disposeQuery]);

  return (
    <SeriesArticleContextProvider>
      <Container>
        <BannerAdSlot>
          {provider.adslot({
            adUnit: "d_banner1",
            breakpoint: theme.breakpoints.up("desktop"),
            sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
          })}
        </BannerAdSlot>

        <StyledSeriesArticleBanner ref={captureIntersectionElement} reference={article} />

        <StyledSeriesArticlePager
          $stickyStackedHeight={60}
          isSticky={isPagerSticky}
          reference={article}
        />

        <MobileAdSlotOne>
          {provider.adslot({
            adUnit: "m_banner1",
            breakpoint: theme.breakpoints.only("mobile"),
            sizes: [[300, 250], "fluid"],
            withBackground: true,
            withLabel: true,
          })}
        </MobileAdSlotOne>

        {article.series?.filter(notEmpty)?.map((series, index, array) => (
          <>
            <StyledSeriesArticleItem
              isSmallImage={index > 0}
              key={index}
              layout={index % 2 === 0 ? "left" : "right"}
              number={`${index + 1}`}
              reference={series}
            />

            {index === 1 && array.length > 2 && (
              <>
                <MobileAdSlotTwo>
                  {provider.adslot({
                    adUnit: "m_lrec1",
                    breakpoint: theme.breakpoints.down("desktop"),
                    sizes: [[300, 250], "fluid"],
                    withBackground: true,
                    withLabel: true,
                  })}
                </MobileAdSlotTwo>
                <SecondBannerAdSlot>
                  {provider.adslot({
                    adUnit: "d_banner2",
                    breakpoint: theme.breakpoints.up("desktop"),
                    sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
                  })}
                </SecondBannerAdSlot>
              </>
            )}

            {index === 2 && (
              <NewsletterWidgetContainer>
                <NewsletterWidgetWrapper>
                  <StyledContentNewsletterWidget
                    reference={article}
                    sequence={["section", "article"]}
                    variant={NewsletterVariant.LiveArticle}
                  />
                </NewsletterWidgetWrapper>
              </NewsletterWidgetContainer>
            )}

            {index === 3 && array.length > 4 && (
              <>
                <MobileAdSlotTwo>
                  {provider.adslot({
                    adUnit: "m_lrec2",
                    breakpoint: theme.breakpoints.down("desktop"),
                    sizes: [[300, 250], "fluid"],
                    withBackground: true,
                    withLabel: true,
                  })}
                </MobileAdSlotTwo>
                <BannerAdSlot>
                  {provider.adslot({
                    adUnit: "d_banner2",
                    breakpoint: theme.breakpoints.up("desktop"),
                    sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
                  })}
                </BannerAdSlot>
              </>
            )}
          </>
        ))}

        <ShareBar>
          <ShareDivider />
          <StyledContentShareWidget
            extraProps={{
              copyLink: {
                disablePortal: false,
                tooltipPlacement: "bottom",
                variant: "tooltip",
              },
            }}
            position="series_bottom"
            reference={article}
            style={{
              backgroundColor: "transparent",
              color: "#001246",
            }}
            types={["facebook", "twitter", "copy-link", "email"]}
          />
        </ShareBar>

        <NativeAdSlot>
          {provider.adslot({
            adUnit: "native5",
            sizes: ["fluid"],
          })}
        </NativeAdSlot>

        <MobileAdSlotThree>
          {provider.adslot({
            adUnit: "m_lrec3",
            breakpoint: theme.breakpoints.down("desktop"),
            sizes: [[300, 250], "fluid"],
            withBackground: true,
            withLabel: true,
          })}
        </MobileAdSlotThree>

        <BannerAdSlot>
          {provider.adslot({
            adUnit: "d_banner2",
            breakpoint: theme.breakpoints.up("desktop"),
            sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
          })}
        </BannerAdSlot>

        <ClientSideSuspense fallback={<></>}>
          {queryReference && <StyledSeriesArticleOtherScmpSeries queryReference={queryReference} />}
        </ClientSideSuspense>
      </Container>
    </SeriesArticleContextProvider>
  );
};

SeriesArticle.displayName = "SeriesArticle";
